import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EntityBadge } from "@/components/common/Visuals";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { BackLink } from "@/components/common/Detail";
import { PersonCard } from "@/components/persons/PersonViews";
import { OrgCard } from "@/components/orgs/OrgViews";
import { plural } from "@/lib/format";

export default function ExpertiseDetailPage() {
  const { id } = useParams();
  const { isAuthed } = useAuth();
  const exp = useQuery({ queryKey: ["expertise", id], queryFn: async () => (await api.get(`/expertises/${id}`)).data });
  const name = exp.data?.name;
  const qs = name ? `expertise=${encodeURIComponent(name)}` : "";
  const persons = useQuery({ queryKey: ["persons", "expertise", name, isAuthed], queryFn: async () => (await api.get(`/persons?${qs}&page_size=6`)).data, enabled: !!name });
  const orgs = useQuery({ queryKey: ["organizations", "expertise", name], queryFn: async () => (await api.get(`/organizations?${qs}&page_size=6`)).data, enabled: !!name && exp.data.organizations_enabled });

  if (exp.isLoading) return <div className="lm-container py-10"><LoadingGrid count={3} /></div>;
  if (exp.isError) return <div className="lm-container py-10"><ErrorState onRetry={exp.refetch} /></div>;
  const e = exp.data;
  return (
    <div className="lm-container pb-20">
      <BackLink to="/expertises" label="Expertises" />
      <section className="lm-card p-6 sm:p-8 lm-fade-up" data-testid="expertise-detail-header" style={{ backgroundImage: `linear-gradient(135deg, ${e.color}1F 0%, transparent 55%)` }}>
        <EntityBadge type="expertises" />
        <h1 className="mt-3 flex items-center gap-3 text-3xl font-extrabold sm:text-4xl" style={{ color: e.color }} data-testid="expertise-detail-name"><Tag className="h-8 w-8" /> {e.name}</h1>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="rounded-full px-5 font-semibold" style={{ backgroundColor: e.color }} data-testid="expertise-open-persons-button">
            <Link to={`/personnes?${qs}`}>{plural(e.persons_count, "personne", "personnes")} <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          {e.organizations_enabled ? (
            <Button asChild variant="outline" className="rounded-full bg-white" data-testid="expertise-open-orgs-button">
              <Link to={`/entreprises?${qs}`}><Building2 className="h-4 w-4" /> {plural(e.organizations_count, "entreprise", "entreprises")}</Link>
            </Button>
          ) : <span className="self-center text-xs text-muted-foreground">La propriété « Expertises » n'existe pas encore dans Maisonna.</span>}
        </div>
      </section>
      <h2 className="mb-4 mt-10 text-xl font-extrabold">Personnes</h2>
      {persons.isLoading ? <LoadingGrid count={3} /> : !persons.data?.items.length ? <EmptyState title="Aucune personne pour cette expertise" /> : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="expertise-persons-grid">{persons.data.items.map((p) => <PersonCard key={p.id} person={p} />)}</div>
      )}
      {e.organizations_enabled && (
        <>
          <h2 className="mb-4 mt-10 text-xl font-extrabold">Entreprises</h2>
          {!orgs.data?.items.length ? <EmptyState title="Aucune entreprise pour cette expertise" /> : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="expertise-orgs-grid">{orgs.data.items.map((o) => <OrgCard key={o.id} org={o} />)}</div>
          )}
        </>
      )}
    </div>
  );
}
